import React from "react";

import {
  CloseRounded as CloseRoundedIcon,
  ContentCopyRounded as ContentCopyRoundedIcon,
  Facebook as FacebookIcon,
  FlagRounded as FlagRoundedIcon,
  LinkedIn as LinkedInIcon,
  RssFeedRounded as RssFeedRoundedIcon,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import { Box, colors, Icon, IconButton, Tooltip, Typography } from "@mui/material";

import { FlaggingAnalyticsService, UserAnalyticsService } from "@analytics/index";

import FlagDataModalTyped from "@components/Forms/FlagData";

import { CompanyType } from "@interfaces/company.type";

import { PopupActions } from "@store/popupStore";

import copyToClipboard from "../utilities/copyToClipboard";

import CompanyDetailsDrawerStyles from "./CompanyDetailsDrawerStyles";
interface ICompanyDetailsDrawerHeaderProperties {
  close: () => void;
  company: CompanyType;
}

export default function CompanyDetailsDrawerHeader({ close, company }: ICompanyDetailsDrawerHeaderProperties) {
  if (!company) {
    return null;
  }
  return (
    <Box sx={CompanyDetailsDrawerStyles.header}>
      <Box
        sx={{
          alignItems: {
            md: "center",
            xs: "flex-start",
          },
          display: "flex",
          flex: 1,
          flexDirection: {
            md: "row",
            xs: "column",
          },
          gap: 3,
          order: {
            md: 1,
            xs: 2,
          },
        }}
      >
        {company.logoUrl && (
          <Box
            alt={`${company.name} logo`}
            component="img"
            onError={(event) => {
              const img = event.currentTarget as HTMLImageElement;
              img.style.display = "none";
            }}
            onLoad={(event) => {
              const img = event.currentTarget as HTMLImageElement;
              if (img.naturalWidth > img.naturalHeight) {
                img.style.width = "auto";
                img.style.height = "100%";
              } else {
                img.style.width = "100%";
                img.style.height = "auto";
              }
            }}
            src={company.logoUrl}
            sx={CompanyDetailsDrawerStyles.logo}
          />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <Typography sx={CompanyDetailsDrawerStyles.companyName} variant="h4">
              {company.name}
              &nbsp;&nbsp;|
            </Typography>
            <Box alignItems="center" display="flex">
              <a
                href={`//${company.primaryDomain || company.websiteUrl}`}
                onClick={() =>
                  UserAnalyticsService.trackOutboundClick(
                    (company.primaryDomain ?? company.websiteUrl) || "",
                    "company_website",
                  )
                }
                rel="noreferrer"
                target="_blank"
              >
                {company.primaryDomain || company.websiteUrl}
              </a>
              <Tooltip title="Copy URL to clipboard">
                <IconButton
                  onClick={() => copyToClipboard(company.primaryDomain || company.websiteUrl, "URL")}
                  size="small"
                >
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "row",
              gap: 1.75,
            }}
          >
            {company.linkedinUrl && (
              <LinkedInIcon
                onClick={(event) => {
                  if (company.linkedinUrl) {
                    UserAnalyticsService.handleLinkAnalytics(event, company.linkedinUrl, "linkedin");
                  }
                }}
                sx={CompanyDetailsDrawerStyles.socialIcon}
              />
            )}
            {company.facebookUrl && (
              <FacebookIcon
                onClick={(event) => {
                  if (company.facebookUrl) {
                    UserAnalyticsService.handleLinkAnalytics(event, company.facebookUrl, "facebook");
                  }
                }}
                sx={CompanyDetailsDrawerStyles.socialIcon}
              />
            )}
            {company.twitterUrl && (
              <XIcon
                onClick={(event) => {
                  if (company.twitterUrl) {
                    UserAnalyticsService.handleLinkAnalytics(event, company.twitterUrl, "twitter");
                  }
                }}
                sx={{
                  ...CompanyDetailsDrawerStyles.socialIcon,
                  fontSize: "20px",
                }}
              />
            )}
            {company.blogUrl && (
              <RssFeedRoundedIcon
                onClick={(event) => {
                  if (company.blogUrl) {
                    UserAnalyticsService.handleLinkAnalytics(event, company.blogUrl, "blog");
                  }
                }}
                sx={CompanyDetailsDrawerStyles.socialIcon}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          border: `1px solid ${colors.grey[400]}`,
          borderRadius: 1,
          display: "inline-flex",
          order: {
            md: 2,
            xs: 1,
          },
          overflow: "hidden",
        }}
      >
        <Tooltip placement="bottom" title="Flag company data issues for review.">
          <Icon
            fontSize="large"
            onClick={() => {
              PopupActions.openModal({
                content: (
                  <FlagDataModalTyped
                    flagCompanyIssue={(feedback, issue) => {
                      FlaggingAnalyticsService.dataIssueFlagged(company.id, issue, feedback, "company");
                      PopupActions.closeModal();
                    }}
                    form="company"
                  />
                ),
                title: "Let's flag this company",
              });
            }}
            sx={CompanyDetailsDrawerStyles.actionsIcon}
          >
            <FlagRoundedIcon className="flag-icon" />
          </Icon>
        </Tooltip>
        <Tooltip placement="bottom" title="Close company details.">
          <Icon onClick={close} sx={CompanyDetailsDrawerStyles.actionsIcon}>
            <CloseRoundedIcon className="close-icon" />
          </Icon>
        </Tooltip>
      </Box>
    </Box>
  );
}
